<template>
  <!-- Password Protection Modal -->
  <DialogModal
    v-model="modalOpen"
    modal-type="password-protected"
    ref="passwordModal">
    <template #header>
      {{ t.modal.enterPassword }}
    </template>

    <template #content>
      <div class="password-protected-modal__content">
        <!-- Error message -->
        <div
          v-if="errorMessage"
          class="messages messages--error password-protected-modal__error">
          {{ errorMessage }}
        </div>
        <p class="password-protected-modal__description">
          {{ t.modal.description }}
        </p>

        <form
          @submit.prevent="handlePasswordSubmit"
          class="password-protected-modal__form webform-submission-form">
          <div
            class="password-protected-modal__input-group"
            :class="[
              'js-form-item',
              'form-item',
              'js-form-type-password',
              'form-item-password',
              'js-form-item-password',
              { 'form-item--error': errorMessage },
            ]">
            <input
              id="password-input"
              type="password"
              v-model="password"
              class="password-protected-modal__input"
              :class="['form-password', 'required', { error: errorMessage }]"
              :placeholder="t.modal.enterPassword"
              :disabled="isSubmitting"
              aria-required="true"
              :aria-invalid="errorMessage ? 'true' : 'false'"
              required
              @keyup.enter="handlePasswordSubmit" />

            <label
              for="password-input"
              class="password-protected-modal__label"
              :class="['js-form-required', 'form-required']">
              {{ t.modal.password }}
            </label>
          </div>

          <div class="password-protected-modal__actions">
            <button
              type="submit"
              class="button button--full"
              :disabled="!password.trim() || isSubmitting">
              {{ isSubmitting ? t.modal.submitting : t.modal.submitButton }}
            </button>
          </div>
        </form>
      </div>
    </template>
  </DialogModal>
</template>

<script>
import DialogModal from './elements/DialogModal';
import downloadProtectedMedia from '../translations/downloadProtectedMedia';

export default {
  name: 'DownloadPasswordProtectedMediaModal',
  components: {
    DialogModal,
  },
  props: {
    mediaId: {
      type: [String, Number],
      required: true,
    },
    apiEndpoint: {
      type: String,
      default: '/api/verify-password-download',
    },
  },
  emits: ['download-success', 'download-error'],
  data() {
    return {
      modalOpen: false,
      password: '',
      errorMessage: '',
      isSubmitting: false,
      t: downloadProtectedMedia,
      controller: null,
    };
  },
  methods: {
    openPasswordModal() {
      this.modalOpen = true;
      this.resetForm();

      this.$nextTick(() => {
        this.$refs.passwordModal?.openDialogModal();
        // Focus password input for better UX
        document.getElementById('password-input')?.focus();
      });
    },

    async handlePasswordSubmit() {
      const trimmedPassword = this.password.trim();

      if (!trimmedPassword) {
        this.setError(this.t.modal.passwordRequired);
        return;
      }

      this.isSubmitting = true;
      this.errorMessage = '';

      try {
        const response = await this.verifyPasswordAndDownload(trimmedPassword);

        // Handle successful response
        if (response.downloadUrl) {
          this.triggerDownload(response.downloadUrl, response.filename);
        } else if (response.success) {
          // Handle cases where download is triggered server-side
          this.$emit('download-success', { mediaId: this.mediaId });
        }

        this.closeModal();
        this.$emit('download-success', { mediaId: this.mediaId, response });
      } catch (error) {
        this.handleDownloadError(error);
      } finally {
        this.isSubmitting = false;
      }
    },

    async verifyPasswordAndDownload(password) {
      this.controller = new AbortController();
      const timeoutId = setTimeout(() => this.controller.abort(), 30000); // 30s timeout

      try {
        const response = await fetch(this.apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            password,
            mediaId: this.mediaId,
          }),
          signal: this.controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || this.t.modal.invalidPassword);
        }

        return await response.json();
      } catch (error) {
        clearTimeout(timeoutId);

        if (error.name === 'AbortError') {
          throw new Error(this.t.modal.timeoutError);
        }
        throw error;
      }
    },

    async triggerDownload(url, filename = null) {
      const response = await fetch(url);
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    },

    handleDownloadError(error) {
      this.setError(error.message || this.t.modal.downloadError);
      this.$emit('download-error', error);
    },

    setError(message) {
      this.errorMessage = message;
      // Focus back to input for retry
      this.$nextTick(() => {
        document.getElementById('password-input')?.focus();
      });
    },

    resetForm() {
      this.password = '';
      this.errorMessage = '';
      this.isSubmitting = false;
    },

    closeModal() {
      this.modalOpen = false;
      this.resetForm();
      this.$refs.passwordModal?.closeDialogModal();
    },
  },

  // Clean up on component destroy
  beforeUnmount() {
    // Cancel any pending requests if component is destroyed
    if (this.controller) {
      this.controller.abort();
    }
  },
};
</script>
